import { useState, useEffect } from "react";
import "./Style.css";
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import { Button, Form, Row, Col, Modal } from "react-bootstrap";
import { Oval } from "react-loader-spinner";
import Select from 'react-select';

export default function EditCredit({ showEdit, closeModal, refreshPage, order }) {
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [subCategoriesMap, setSubCategoriesMap] = useState({});
  const [productsMap, setProductsMap] = useState({});
  const [formData, setFormData] = useState([]);
  const [companies, setCompanies] = useState([]); // Store companies once
  const [saleSupervisorCheck, setsaleSupervisorCheck] = useState(null);
  

  const storedPermissions = localStorage.getItem("userPermissions");
  const permissions = storedPermissions ? JSON.parse(storedPermissions) : [];

  const userDataString = localStorage.getItem("userData");
  let userTypeId = null;

  if (userDataString) {
    const userData = JSON.parse(userDataString);
    userTypeId = userData.user.user_type_id;
  } else {
    console.log("No user data found");
  }

  const hasPermission = (permission) => {
    return Array.isArray(permissions) && permissions.includes(permission);
  };

  // Fetch orders on mount
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);

      if (order) {
        try {
          const response = await axiosInstance.get("/user/order/get_order_no_paginate", {
            params: {
              order_code: order.order_code,
            },
          });
          setOrderList(response.data.data.orders);
          setsaleSupervisorCheck(response.data.data.user_check);
          

        } catch (error) {
          console.error("Error fetching orders:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchOrders();
  }, [order]);

  // Fetch categories and customers on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const customerResponse = await axiosInstance.get("/user/customer/fetch_customer_no_paginate");
        setCustomers(customerResponse.data.data.customer);

        const categoryResponse = await axiosInstance.get("/user/category/fetch");
        setCategories(categoryResponse.data.data.category.data);
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Populate formData when orderList is updated and fetch companies, subcategories, and products
  useEffect(() => {
    if (orderList.length > 0) {
      setIsLoading(true);
      const initialFormData = orderList.map(async (orderItem, index) => {
        // Fetch companies for the selected customer
        try {
          const companyResponse = await axiosInstance.get(`/user/customer/fetch_customer_company/${order.customer_id}`);
          setCompanies(companyResponse.data.data.company);
        } catch (error) {
          console.error("Error fetching companies:", error);
        }

        // Fetch subcategories for the selected category
        try {
          const subCategoryResponse = await axiosInstance.get(`/user/category/getSubCategory?category_id=${order.category_id}`);
          setSubCategoriesMap((prev) => ({
            ...prev,
            [index]: subCategoryResponse.data.data.subCategories,
          }));
        } catch (error) {
          console.error("Error fetching subcategories:", error);
        }

        // Fetch products for the selected subcategory
        try {
          const productResponse = await axiosInstance.get(`/user/order/get_product_by_subcategory?sub_category_id=${order.sub_category_id}`);
          setProductsMap((prev) => ({
            ...prev,
            [index]: productResponse.data.data.products,
          }));
        } catch (error) {
          console.error("Error fetching products:", error);
        }

        return {
          ...orderItem,
          id: order.id,
          customer_id: order.customer_id || "",
          category_id: order.category_id || "",
          sub_category_id: order.sub_category_id || "",
          company_detail_id: order.company_detail_id || "",
          product_id: order.product_id || "",
          quantity: order.quantity || 1,
          price: order.price || "0",
          payment_type: order.payment_type || "",
          discount: order.discount || "0",
          total_price: order.total_price || "",
          status: order.status || "",
          warehouse_house_id: order.ware_house_name || "",
          warehouse_id: order.warehouse_house_id || "",
          available_quantity: order.ware_house_quanity || "",
        };
      });

      Promise.all(initialFormData).then((filledData) => {
        setFormData(filledData);
        setIsLoading(false);
      });
    }
  }, [orderList]);

  // Handle customer change with react-select
  const handleCustomerChange = async (selectedOption) => {
    const customerId = selectedOption ? selectedOption.value : "";
    const newFormData = [...formData];

    newFormData.forEach((item) => {
      item.customer_id = customerId;
      item.company_detail_id = ""; // Reset company selection
    });

    setFormData(newFormData);
    setCompanies([]); 

    if (customerId) {
      try {
        const response = await axiosInstance.get(`/user/customer/fetch_customer_company/${customerId}`);
        setCompanies(response.data.data.company);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    }
  };

  // Handle company change with react-select
  const handleCompanyChange = (selectedOption) => {
    const companyId = selectedOption ? selectedOption.value : "";
    const newFormData = [...formData];
    newFormData[0].company_detail_id = companyId; // Assuming company is shared across all orders
    setFormData(newFormData);
  };

  const handleCategoryChange = async (e, index) => {
    const categoryId = e.target.value;
    const newFormData = [...formData];

    newFormData[index] = {
      ...newFormData[index],
      category_id: categoryId,
      sub_category_id: "",
      product_id: "",
      price: "",
    };

    setFormData(newFormData);
    setSubCategoriesMap((prev) => ({ ...prev, [index]: [] }));
    setProductsMap((prev) => ({ ...prev, [index]: [] }));

    try {
      const subCategoryResponse = await axiosInstance.get(
        `/user/category/getSubCategory?category_id=${categoryId}`
      );
      setSubCategoriesMap((prev) => ({
        ...prev,
        [index]: subCategoryResponse.data.data.subCategories,
      }));
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  const handleSubCategoryChange = async (e, index) => {
    const subCategoryId = e.target.value;
    const newFormData = [...formData];

    newFormData[index] = {
      ...newFormData[index],
      sub_category_id: subCategoryId,
      product_id: "",
      price: "",
    };

    setFormData(newFormData);
    setProductsMap((prev) => ({ ...prev, [index]: [] }));

    try {
      const productResponse = await axiosInstance.get(
        `/user/order/get_product_by_subcategory?sub_category_id=${subCategoryId}`
      );
      setProductsMap((prev) => ({
        ...prev,
        [index]: productResponse.data.data.products,
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleProductChange = async (e, index) => {
    const productId = e.target.value;
    const selectedProduct = productsMap[index]?.find((p) => p.id === parseInt(productId));
    const newFormData = [...formData];

    newFormData[index] = {
      ...newFormData[index],
      product_id: productId,
      price: selectedProduct ? selectedProduct.price : "",
    };

    setFormData(newFormData);
    updateTotalPrice(index);

    if (productId) {
      try {
        const warehouseResponse = await axiosInstance.get(`/user/check_in/get-checkIn/${productId}`);
        const warehouseData = warehouseResponse.data.data.item;
        const warehouseId = warehouseData.name === "Sagamu" ? 2 : warehouseData.name === "Asaba" ? 1 : warehouseData.id;

        newFormData[index] = {
          ...newFormData[index],
          warehouse_house_id: warehouseData.name,
          warehouse_id: warehouseId,
          available_quantity: warehouseData.quantity,
        };

        setFormData(newFormData);
      } catch (error) {
        console.error("Error fetching warehouse data:", error);
      }
    }
  };

  const updateTotalPrice = (index) => {
    const newFormData = [...formData];
    const quantity = parseInt(newFormData[index].quantity) || 1;
    const price = parseFloat(newFormData[index].price) || 0;
    const discountPercentage = parseFloat(newFormData[index].discount) || 0;

    const subtotal = quantity * price;
    const discountAmount = (subtotal * discountPercentage) / 100;
    newFormData[index].total_price = (subtotal - discountAmount).toFixed(2);
    setFormData(newFormData);
  };

  const incrementQuantity = (index) => {
    const newFormData = [...formData];
    newFormData[index].quantity = (parseInt(newFormData[index].quantity) || 1) + 1;
    setFormData(newFormData);
    updateTotalPrice(index);
  };

  const decrementQuantity = (index) => {
    const newFormData = [...formData];
    const currentQuantity = parseInt(newFormData[index].quantity) || 1;
    newFormData[index].quantity = currentQuantity > 1 ? currentQuantity - 1 : 1;
    setFormData(newFormData);
    updateTotalPrice(index);
  };

  const handleDiscountChange = (e, index) => {
    const newFormData = [...formData];
    // Ensure discount is not negative and defaults to 0 if empty
    const value = e.target.value;
    const numericValue = value === "" ? "0" : Math.max(0, parseFloat(value) || 0).toString();
    newFormData[index].discount = numericValue;
    setFormData(newFormData);
    updateTotalPrice(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const payload = {
      customer_id: formData[0]?.customer_id,
      company_detail_id: formData[0]?.company_detail_id,
      warehouse_id: formData[0]?.warehouse_id,
      orders: formData.map((order) => ({
        id: order.id,
        product_id: order.product_id,
        category_id: order.category_id,
        sub_category_id: order.sub_category_id,
        price: order.price,
        quantity: order.quantity,
        discount: order.discount,
        total_price: order.total_price,
        payment_type: order.payment_type,
        warehouse_id: order.warehouse_id,
        status: order.status,
      })),
    };

    try {
      const response = await axiosInstance.post("/user/order/edit", payload);
      toast.success("Order info edited successfully");
      closeModal();
      refreshPage();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    closeModal();
  };

  // Transform data for react-select
  const customerOptions = customers.map((customer) => ({
    label: `${customer.first_name} ${customer.last_name}`,
    value: customer.customer_id,
  }));

  const companyOptions = companies.map((company) => ({
    label: company.company_name,
    value: company.id,
  }));

  return (
    <div className="CreateCSP">
      <Modal show={showEdit} onHide={closeModal} dialogClassName="modal-lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Order Details</Modal.Title>
          </Modal.Header>
          {isLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "80vh" }}
            >
              <Oval
                height={80}
                width={80}
                color="#FF0200"
                visible={true}
                ariaLabel="oval-loading"
                secondaryColor="#4fa94d"
                strokeWidth={2}
                strokeWidthSecondary={2}
              />
            </div>
          ) : (
            <Modal.Body>
              <Row>
                {/* Customer Selection with react-select */}
                <Col md={6} lg={6} xs={12} sm={12} className="mb-3">
                  <Form.Group>
                    <Form.Label><b>Customer</b></Form.Label>
                    <Select
                      options={customerOptions}
                      onChange={handleCustomerChange}
                      value={customerOptions.find((opt) => opt.value === formData[0]?.customer_id) || null}
                      isSearchable
                      placeholder="-- Select Customer --"
                    />
                  </Form.Group>
                </Col>
                {/* Company Selection with react-select */}
                <Col md={6} lg={6} xs={12} sm={12} className="mb-3">
                  <Form.Group>
                    <Form.Label><b>Company</b></Form.Label>
                    <Select
                      options={companyOptions}
                      onChange={handleCompanyChange}
                      value={companyOptions.find((opt) => opt.value === formData[0]?.company_detail_id) || null}
                      isSearchable
                      placeholder="-- Select Company --"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {formData.map((orderItem, index) => {
                const currentFormData = formData[index] || {};
                const subcategories = subCategoriesMap[index] || [];
                const products = productsMap[index] || [];

                return (
                  <div key={orderItem.id} className="order-item mb-3">
                    <div className="mb-2 p-2" style={{ border: '1px dotted rgba(0, 0, 0, 0.5)' }}>
                      <button className="btn btn-info d-flex justify-content-center mx-auto mt-1 mb-4" disabled>Item {index + 1}</button>
                      <Row>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Category</b></Form.Label>
                            <Form.Control
                              as="select"
                              name="category_id"
                              value={currentFormData.category_id || ""}
                              onChange={(e) => handleCategoryChange(e, index)}
                            >
                              <option value="">-- Select Category --</option>
                              {categories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Subcategory</b></Form.Label>
                            <Form.Control
                              as="select"
                              name="sub_category_id"
                              value={currentFormData.sub_category_id || ""}
                              onChange={(e) => handleSubCategoryChange(e, index)}
                            >
                              <option value="">-- Select Subcategory --</option>
                              {subcategories.map((subCategory) => (
                                <option key={subCategory.id} value={subCategory.id}>
                                  {subCategory.name}
                                </option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Product</b></Form.Label>
                            <Form.Control
                              as="select"
                              name="product_id"
                              value={currentFormData.product_id || ""}
                              onChange={(e) => handleProductChange(e, index)}
                            >
                              <option value="">-- Select Product --</option>
                              {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.product_name}
                                </option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Warehouse</b></Form.Label>
                            <Form.Control
                              type="text"
                              value={currentFormData.warehouse_house_id}
                              readOnly
                              style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Available Quantity</b></Form.Label>
                            <Form.Control
                              type="text"
                              value={
                                currentFormData.product_id && (currentFormData.available_quantity === 0 || currentFormData.available_quantity === null || currentFormData.available_quantity === "")
                                  ? "Out of Stock"
                                  : currentFormData.available_quantity
                              }
                              readOnly
                              style={{
                                backgroundColor: "#f0f0f0",
                                color: currentFormData.product_id && (currentFormData.available_quantity === 0 || currentFormData.available_quantity === null || currentFormData.available_quantity === "") ? "red" : "#6c757d",
                                fontWeight: currentFormData.product_id && (currentFormData.available_quantity === 0 || currentFormData.available_quantity === null || currentFormData.available_quantity === "") ? "bold" : "normal"
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Quantity</b></Form.Label>
                            <div className="d-flex align-items-center">
                              <Button
                                variant="secondary"
                                onClick={() => decrementQuantity(index)}
                                className="me-2"
                              >
                                -
                              </Button>
                              <Form.Control
                                type="number"
                                name="quantity"
                                value={currentFormData.quantity || 1}
                                onChange={(e) => {
                                  const newFormData = [...formData];
                                  newFormData[index].quantity = e.target.value;
                                  setFormData(newFormData);
                                  updateTotalPrice(index);
                                }}
                                style={{ width: '70px', textAlign: 'center' }}
                              />
                              <Button
                                variant="secondary"
                                onClick={() => incrementQuantity(index)}
                                className="ms-2"
                              >
                                +
                              </Button>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Price</b></Form.Label>
                            <Form.Control
                              type="number"
                              name="price"
                              value={currentFormData.price || ""}
                              onChange={(e) => {
                                const newFormData = [...formData];
                                newFormData[index].price = e.target.value;
                                setFormData(newFormData);
                                updateTotalPrice(index);
                              }}
                              style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Total Price</b></Form.Label>
                            <Form.Control
                              type="number"
                              name="total_price"
                              value={currentFormData.total_price || ""}
                              onChange={(e) => {
                                const newFormData = [...formData];
                                newFormData[index].total_price = e.target.value;
                                setFormData(newFormData);
                              }}
                              style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Payment Type</b></Form.Label>
                            <Form.Control
                              as="select"
                              name="payment_type"
                              value={currentFormData.payment_type || ""}
                              onChange={(e) => {
                                const newFormData = [...formData];
                                newFormData[index].payment_type = e.target.value;
                                setFormData(newFormData);
                              }}
                            >
                              <option value="">-- Select Payment Type --</option>
                              <option value="cash">Cash/Prepaid</option>
                              <option value="credit">Credit</option>
                              <option value="transfer">Pay on Delivery</option>
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                          <Form.Group>
                            <Form.Label><b>Discount (%)</b></Form.Label>
                            <Form.Control
                              type="number"
                              name="discount"
                              value={currentFormData.discount || ""}
                              onChange={(e) => handleDiscountChange(e, index)}
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                        {((userTypeId === 1 || userTypeId === 2 || userTypeId === 3) && (saleSupervisorCheck.status !== true)) && (
                          <Col md={4} lg={4} xs={12} sm={12} className="mb-3">
                            <Form.Group>
                              <Form.Label><b>Approval</b></Form.Label>
                              <Form.Control
                                as="select"
                                name="status"
                                value={currentFormData.status || ""}
                                onChange={(e) => {
                                  const newFormData = [...formData];
                                  newFormData[index].status = e.target.value;
                                  setFormData(newFormData);
                                }}
                              >
                                <option value="">-- Select Status --</option>
                                <option value="approved">Approved</option>
                                <option value="pending">Pending</option>
                                <option value="reject">Rejected</option>
                              </Form.Control>
                            </Form.Group>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </div>
                );
              })}
            </Modal.Body>
          )}
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? "saving..." : "Submit"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}